import {
    type Ref,
    ref,
    computed,
    watch,
    type ComputedRef,
    type WritableComputedRef,
} from 'vue'
import { onKeyUp } from '@vueuse/core'

import { getTimeout } from '@/helpers/globals'

import type { GalleryWithEnquiryCardFragment } from '@/graphql/api'

const INITIALLY_DISPLAYED_ITEMS_COUNT = 6
const STEP_DURATION = 400
const STEP_DELAY_RATIO = 0.1
const START_TIME = STEP_DURATION * STEP_DELAY_RATIO

function isHtmlElement(el: unknown): el is HTMLElement {
    return el instanceof HTMLElement
}

export interface ComponentGalleryWithEnquiryDesktop {
    showMore: () => void
    itemsDisplayed: ComputedRef<GalleryWithEnquiryCardFragment[]>
    isDesktopModalOpen: ComputedRef<boolean>
    openDesktopModal: (item: GalleryWithEnquiryCardFragment) => void
    closeDesktopModal: () => void
    desktopModalTransitionName: Ref<string, string>
    areAllItemsDislpayed: Ref<boolean, boolean>
    onEnter: (el: Element, done: () => void) => void
    onAfterEnter: (el: Element) => void
    previous: () => void
    next: () => void
}

export function useComponentGalleryWithEnquiryDesktop(
    isMobile: ComputedRef<boolean>,
    items: Readonly<Ref<GalleryWithEnquiryCardFragment[]>>,
    currentItem: WritableComputedRef<
        GalleryWithEnquiryCardFragment | undefined
    >,
    currentItemIndex: ComputedRef<number>
): ComponentGalleryWithEnquiryDesktop {
    const areAllItemsDislpayed = ref(false)
    const showMore = () => {
        areAllItemsDislpayed.value = true
    }
    const itemsDisplayed = computed(() =>
        areAllItemsDislpayed.value
            ? items.value
            : items.value.slice(0, INITIALLY_DISPLAYED_ITEMS_COUNT)
    )

    const isDesktopModalOpen = computed(
        () => !isMobile.value && Boolean(currentItem.value)
    )

    const openDesktopModal = (item: GalleryWithEnquiryCardFragment) => {
        currentItem.value = item
    }

    const closeDesktopModal = () => {
        currentItem.value = undefined
    }

    const desktopModalTransitionName = ref('slide')

    const next = () => {
        const nextIndex = currentItemIndex.value + 1

        if (nextIndex < items.value.length) {
            desktopModalTransitionName.value = 'slide'

            currentItem.value = items.value[nextIndex]
        }
    }

    const previous = () => {
        const prevIndex = currentItemIndex.value - 1

        if (prevIndex >= 0) {
            desktopModalTransitionName.value = 'slide-reverse'

            currentItem.value = items.value[prevIndex]
        }
    }

    onKeyUp('ArrowLeft', () => {
        if (isDesktopModalOpen.value) {
            previous()
        }
    })

    onKeyUp('ArrowRight', () => {
        if (isDesktopModalOpen.value) {
            next()
        }
    })

    watch(currentItemIndex, (newCurrent, oldCurrent) => {
        if (
            newCurrent >= INITIALLY_DISPLAYED_ITEMS_COUNT &&
            oldCurrent < INITIALLY_DISPLAYED_ITEMS_COUNT
        ) {
            showMore()
        }
    })

    const setTimeout = getTimeout()?.setTimeout

    const onEnter = (el: Element, done: () => void) => {
        if (!isHtmlElement(el)) {
            return
        }

        if (!setTimeout) {
            return
        }

        const index =
            Number(el.dataset['index']) - INITIALLY_DISPLAYED_ITEMS_COUNT
        const itemStartTime = index * START_TIME

        el.style.opacity = '0'
        el.style.transform = 'translateY(-60px)'
        el.style.transition = `all ${STEP_DURATION}ms ease-out`
        el.style.pointerEvents = 'none'

        setTimeout(() => {
            el.style.opacity = '1'
            el.style.transform = 'translateY(0)'
        }, itemStartTime)

        setTimeout(done, itemStartTime + STEP_DURATION)
    }

    const onAfterEnter = (el: Element) => {
        if (!isHtmlElement(el)) {
            return
        }

        el.style.opacity = ''
        el.style.transform = ''
        el.style.transition = ''
        el.style.pointerEvents = ''
    }

    return {
        showMore,
        itemsDisplayed,
        isDesktopModalOpen,
        openDesktopModal,
        closeDesktopModal,
        desktopModalTransitionName,
        areAllItemsDislpayed,
        onEnter,
        onAfterEnter,
        previous,
        next,
    }
}
